import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpInterceptorFn,
    HttpRequest,
} from '@angular/common/http';

import { inject } from '@angular/core';
import { Router } from '@angular/router';

import {
    catchError,
    finalize,
    Observable,
    retry,
    shareReplay,
    switchMap,
    throwError,
    timer,
} from 'rxjs';

import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { LoginService } from '../../features/login/services/login.service';
import { LoginResponse } from '../../features/login/models/login-response.model';

interface BackendProblemDetails {
    code?: string;
    title?: string;
    detail?: string;
}

const SESSION_REVOKED_CODE = 'auth.session_revoked';
const SESSION_REVOCATION_UNAVAILABLE_CODE =
    'auth.session_revocation_unavailable';

const SESSION_REVOCATION_RETRY_DELAYS_MS = [
    1000,
    2000,
    4000,
];

const SESSION_REVOCATION_TOAST_DURATION_MS = 8000;

let refreshRequest$: Observable<LoginResponse> | null = null;
let revocationUnavailableToastVisibleUntil = 0;

export const authInterceptor: HttpInterceptorFn = (request, next) => {
    const authService = inject(AuthService);
    const loginService = inject(LoginService);
    const router = inject(Router);
    const toastService = inject(ToastService);

    const isAuthCall =
        request.url.includes('/auth/login') ||
        request.url.includes('/auth/refresh') ||
        request.url.includes('/auth/logout');

    if (isAuthCall) {
        return next(
            addAuthHeaders(
                request,
                authService.getToken()
            )
        );
    }

    const refreshToken =
        authService.getRefreshToken();

    /*
     * Preemptive refresh shortly before access-token expiry.
     * The expiry itself comes from the backend response, so the
     * new 30-minute access-token lifetime needs no hardcoded change.
     */
    if (
        refreshToken &&
        authService.shouldRefreshAccessToken()
    ) {
        if (!refreshRequest$) {
            refreshRequest$ =
                loginService
                    .refreshToken(refreshToken)
                    .pipe(
                        shareReplay(1),

                        finalize(() => {
                            refreshRequest$ = null;
                        })
                    );
        }

        return refreshRequest$.pipe(
            switchMap((response) => {
                authService.updateTokens(response);

                return sendWithSessionRevocationBackoff(
                    request,
                    next,
                    response.accessToken,
                    toastService
                );
            }),

            catchError(
                (error: HttpErrorResponse) => {
                    /*
                     * Redis/revocation check is temporarily unavailable.
                     * Keep the current local session and do NOT navigate
                     * to login.
                     */
                    if (
                        isSessionRevocationUnavailable(
                            error
                        )
                    ) {
                        showSessionRevocationUnavailable(
                            toastService,
                            error
                        );

                        return throwError(
                            () => error
                        );
                    }

                    /*
                     * A revoked session is final.
                     * Never try another refresh.
                     */
                    if (
                        isSessionRevoked(error)
                    ) {
                        handleRevokedSession(
                            authService,
                            router,
                            toastService,
                            error
                        );

                        return throwError(
                            () => error
                        );
                    }

                    /*
                     * Existing behaviour for all other
                     * refresh failures remains unchanged.
                     */
                    authService.clearSession();

                    router.navigate([
                        '/login',
                    ]);

                    return throwError(
                        () => error
                    );
                }
            )
        );
    }

    return sendWithSessionRevocationBackoff(
        request,
        next,
        authService.getToken(),
        toastService
    ).pipe(
        catchError(
            (error: HttpErrorResponse) => {
                /*
                 * FV-267:
                 *
                 * 401 + auth.session_revoked
                 *
                 * The backend already removed the refresh token.
                 * Therefore this error must be handled BEFORE the
                 * normal 401 -> refresh behaviour.
                 */
                if (
                    isSessionRevoked(error)
                ) {
                    handleRevokedSession(
                        authService,
                        router,
                        toastService,
                        error
                    );

                    return throwError(
                        () => error
                    );
                }

                /*
                 * FV-267:
                 *
                 * 503 + auth.session_revocation_unavailable
                 *
                 * Do not clear tokens.
                 * Do not refresh.
                 * Do not redirect to login.
                 *
                 * sendWithSessionRevocationBackoff() has already
                 * retried the original request automatically.
                 */
                if (
                    isSessionRevocationUnavailable(
                        error
                    )
                ) {
                    showSessionRevocationUnavailable(
                        toastService,
                        error
                    );

                    return throwError(
                        () => error
                    );
                }

                /*
                 * Existing POS behaviour stays unchanged.
                 */
                if (
                    error.status === 401 &&
                    authService.isPosSession()
                ) {
                    authService.clearSession();

                    router.navigate([
                        '/login',
                    ]);

                    return throwError(
                        () => error
                    );
                }

                /*
                 * Other HTTP errors and 401 responses without
                 * a refresh token keep their previous behaviour.
                 */
                if (
                    error.status !== 401 ||
                    !refreshToken
                ) {
                    return throwError(
                        () => error
                    );
                }

                /*
                 * Existing normal 401 -> refresh flow.
                 */
                if (!refreshRequest$) {
                    refreshRequest$ =
                        loginService
                            .refreshToken(
                                refreshToken
                            )
                            .pipe(
                                shareReplay(1),

                                finalize(() => {
                                    refreshRequest$ =
                                        null;
                                })
                            );
                }

                return refreshRequest$.pipe(
                    switchMap(
                        (response) => {
                            authService.updateTokens(
                                response
                            );

                            return sendWithSessionRevocationBackoff(
                                request,
                                next,
                                response.accessToken,
                                toastService
                            );
                        }
                    ),

                    catchError(
                        (
                            refreshError:
                                HttpErrorResponse
                        ) => {
                            /*
                             * Never turn the special temporary
                             * 503 into a logout.
                             */
                            if (
                                isSessionRevocationUnavailable(
                                    refreshError
                                )
                            ) {
                                showSessionRevocationUnavailable(
                                    toastService,
                                    refreshError
                                );

                                return throwError(
                                    () =>
                                        refreshError
                                );
                            }

                            if (
                                isSessionRevoked(
                                    refreshError
                                )
                            ) {
                                handleRevokedSession(
                                    authService,
                                    router,
                                    toastService,
                                    refreshError
                                );

                                return throwError(
                                    () =>
                                        refreshError
                                );
                            }

                            /*
                             * Existing behaviour for all other
                             * refresh errors.
                             */
                            authService.clearSession();

                            router.navigate([
                                '/login',
                            ]);

                            return throwError(
                                () =>
                                    refreshError
                            );
                        }
                    )
                );
            }
        )
    );
};

function sendWithSessionRevocationBackoff(
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
    token: string | null,
    toastService: ToastService
): Observable<HttpEvent<unknown>> {
    return next(
        addAuthHeaders(
            request,
            token
        )
    ).pipe(
        retry({
            count:
                SESSION_REVOCATION_RETRY_DELAYS_MS.length,

            delay: (
                error: HttpErrorResponse,
                retryCount
            ) => {
                /*
                 * Retry ONLY the new temporary revocation error.
                 * Other 503/401 responses are not affected.
                 */
                if (
                    !isSessionRevocationUnavailable(
                        error
                    )
                ) {
                    return throwError(
                        () => error
                    );
                }

                showSessionRevocationUnavailable(
                    toastService,
                    error
                );

                /*
                 * Backoff:
                 * 1st retry -> 1 second
                 * 2nd retry -> 2 seconds
                 * 3rd retry -> 4 seconds
                 */
                const delayIndex =
                    Math.min(
                        retryCount - 1,

                        SESSION_REVOCATION_RETRY_DELAYS_MS.length -
                        1
                    );

                return timer(
                    SESSION_REVOCATION_RETRY_DELAYS_MS[
                    delayIndex
                    ]
                );
            },
        })
    );
}

function handleRevokedSession(
    authService: AuthService,
    router: Router,
    toastService: ToastService,
    error: HttpErrorResponse
): void {
    const problem =
        getProblemDetails(error);

    /*
     * Remove the complete local session.
     */
    authService.clearSession();

    /*
     * Backend title/detail are already localized.
     */
    toastService.warning(
        problem?.title?.trim() ||
        'Sitzung beendet',

        problem?.detail?.trim() ||
        'Bitte melden Sie sich erneut an.'
    );

    router.navigate([
        '/login',
    ]);
}

function showSessionRevocationUnavailable(
    toastService: ToastService,
    error: HttpErrorResponse
): void {
    const now =
        Date.now();

    /*
     * Multiple parallel API calls may all receive the same
     * 503. Do not spam the user with identical toasts.
     */
    if (
        now <
        revocationUnavailableToastVisibleUntil
    ) {
        return;
    }

    revocationUnavailableToastVisibleUntil =
        now +
        SESSION_REVOCATION_TOAST_DURATION_MS;

    const problem =
        getProblemDetails(error);

    /*
     * Session/token stay completely untouched.
     * The backend text is already localized de-DE / en-US.
     */
    toastService.warning(
        problem?.title?.trim() ||
        'Dienst nicht verfügbar',

        problem?.detail?.trim() ||
        'Der Dienst ist vorübergehend nicht verfügbar. Bitte versuchen Sie es in Kürze erneut.',

        SESSION_REVOCATION_TOAST_DURATION_MS
    );
}

function isSessionRevoked(
    error: HttpErrorResponse
): boolean {
    return (
        error.status === 401 &&
        getProblemDetails(error)
            ?.code ===
        SESSION_REVOKED_CODE
    );
}

function isSessionRevocationUnavailable(
    error: HttpErrorResponse
): boolean {
    return (
        error.status === 503 &&
        getProblemDetails(error)
            ?.code ===
        SESSION_REVOCATION_UNAVAILABLE_CODE
    );
}

function getProblemDetails(
    error: HttpErrorResponse
): BackendProblemDetails | null {
    const value =
        error.error;

    if (
        typeof value !==
        'object' ||
        value === null
    ) {
        return null;
    }

    return value as BackendProblemDetails;
}

function addAuthHeaders(
    request: HttpRequest<unknown>,
    token: string | null
): HttpRequest<unknown> {
    if (!token) {
        return request;
    }

    return request.clone({
        setHeaders: {
            Authorization:
                `Bearer ${token}`,
        },
    });
}